/**
 * How many times you've practised each technique.
 *
 * The seed of a skill tree, and deliberately the smallest version of one: a
 * count and the last verdict, per technique. It records what actually happened
 * rather than simulating progress, which is the whole reason this is worth
 * having — "you've seared six things" is true, and a level number would not be.
 *
 * Same rules as every other store here: reads never throw, and a failed write
 * is swallowed. Losing a practice count is not worth an error message.
 */

import type { TechniqueId } from "./types";

const PRACTICE_KEY = "pantrymind.practice.v1";

/** What the model made of your last attempt, or "untried". */
export type Verdict = "good" | "close" | "off" | "unclear";

export interface PracticeRecord {
  attempts: number;
  /** The most recent verdict, so the list can show where you are. */
  last: Verdict;
  /** The best you've managed, which is the one worth remembering. */
  best: Verdict;
  /** ISO date of the last attempt. */
  lastAt: string;
}

export type PracticeLog = Partial<Record<TechniqueId, PracticeRecord>>;

/** Better is lower. Used to decide whether a new attempt beats the old best. */
const RANK: Record<Verdict, number> = { good: 0, close: 1, off: 2, unclear: 3 };

const isVerdict = (value: unknown): value is Verdict =>
  typeof value === "string" && value in RANK;

export function loadPractice(): PracticeLog {
  try {
    const raw = localStorage.getItem(PRACTICE_KEY);
    if (!raw) return {};

    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return {};

    const log: PracticeLog = {};
    for (const [id, value] of Object.entries(parsed as Record<string, unknown>)) {
      const record = value as Partial<PracticeRecord>;
      // One malformed entry shouldn't cost the rest of the log.
      if (typeof record?.attempts !== "number" || !Number.isFinite(record.attempts)) continue;
      if (!isVerdict(record.last) || !isVerdict(record.best)) continue;

      log[id as TechniqueId] = {
        attempts: Math.max(0, Math.floor(record.attempts)),
        last: record.last,
        best: record.best,
        lastAt: typeof record.lastAt === "string" ? record.lastAt : "",
      };
    }
    return log;
  } catch {
    return {};
  }
}

/**
 * Records one attempt and returns the updated log.
 *
 * `best` only ever improves. Having a good attempt demoted by a later bad one
 * would make the log a record of your most recent mood rather than of what you
 * have actually managed.
 */
export function recordAttempt(id: TechniqueId, verdict: Verdict, today: string): PracticeLog {
  const log = loadPractice();
  const previous = log[id];

  const next: PracticeRecord = {
    attempts: (previous?.attempts ?? 0) + 1,
    last: verdict,
    best:
      previous && RANK[previous.best] <= RANK[verdict] ? previous.best : verdict,
    lastAt: today,
  };

  const updated: PracticeLog = { ...log, [id]: next };

  try {
    localStorage.setItem(PRACTICE_KEY, JSON.stringify(updated));
  } catch {
    // The count still applies for this session; it just won't survive a reload.
  }

  return updated;
}

export function clearPractice(): void {
  try {
    localStorage.removeItem(PRACTICE_KEY);
  } catch {
    // Nothing to do.
  }
}
